import React, { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { Button } from "./button";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import type { CoachSelect } from "~/lib/schemas";
import { gray, purple } from "~/lib/ui";
import { getCDNImageUrl } from "./cdn-image";

const dreamCoachMatchIconDataURL =
  "https://form-data.dreamcoachmatch.com/public/icons/d.svg";
const fbIconDataURL =
  "https://form-data.dreamcoachmatch.com/public/icons/fb.svg";
const websiteIconDataURL =
  "https://form-data.dreamcoachmatch.com/public/icons/website.svg";
const instagramIconDataURL =
  "https://form-data.dreamcoachmatch.com/public/icons/instagram.svg";
const linkedinIconDataURL =
  "https://form-data.dreamcoachmatch.com/public/icons/linkedin.svg";
const youtubeIconDataURL =
  "https://form-data.dreamcoachmatch.com/public/icons/youtube.svg";
const emailIconDataURL =
  "https://form-data.dreamcoachmatch.com/public/icons/email.svg";
const phoneIconDataURL =
  "https://form-data.dreamcoachmatch.com/public/icons/phone.svg";

export interface EmailSignatureProps {
  coach: CoachSelect & {
    profile?: {
      coachingTitle?: string | null;
      title?: string | null;
      mainImage?: string | null;
      website?: string | null;
      facebookProfile?: string | null;
      instagramProfile?: string | null;
      linkedinProfile?: string | null;
      youtubeChannel?: string | null;
    } | null;
  };
}

export const EmailSignature = ({ coach }: EmailSignatureProps) => {
  const [copied, setCopied] = useState(false);
  const [hiddenDiv, setHiddenDiv] = useState<HTMLDivElement>();

  // Create hidden div for rich text copying
  useEffect(() => {
    if (!hiddenDiv) {
      const div = document.createElement("div");
      div.contentEditable = "true";
      div.style.position = "fixed";
      div.style.left = "-9999px";
      div.style.top = "0";
      document.body.appendChild(div);
      setHiddenDiv(div);
    }

    return () => {
      hiddenDiv?.remove();
    };
  }, [hiddenDiv]);

  const signatureHtml = `
    <table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; width: 100%; max-width: 500px;">
      <tr>
        <td>
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="border-top: 1px solid #E5E7EB;">
            <tr><td height="16"></td></tr>
            <tr>
              <td width="96" valign="top">
                ${
                  coach.profilePictureUrl
                    ? `
                  <img src="${getCDNImageUrl(coach.profilePictureUrl, 160)}" alt="${coach.fullName}" width="80" height="80" style="border-radius: 50%; object-fit: cover;">
                `
                    : ""
                }
              </td>
              <td valign="top">
                <table cellpadding="0" cellspacing="0" border="0" width="100%">
                  <tr>
                    <td>
                      <div style="font-family: Arial, sans-serif; font-weight: bold; font-size: 16px; color: #111827;">${coach.fullName}</div>
                    </td>
                  </tr>
                  ${
                    coach.profile?.coachingTitle
                      ? `
                  <tr>
                    <td height="4"></td>
                  </tr>
                  <tr>
                    <td>
                      <div style="font-family: Arial, sans-serif; color: #4B5563;">${coach.profile.coachingTitle}</div>
                    </td>
                  </tr>
                  `
                      : ""
                  }
                  <tr>
                    <td height="8"></td>
                  </tr>
                  <tr>
                    <td>
                      <table cellpadding="0" cellspacing="0" border="0">
                        <tr>
                          <td valign="middle" width="20">
                            <img src="${emailIconDataURL}" alt="Email" width="16" height="16">
                          </td>
                          <td valign="middle">
                            <a href="mailto:${coach.email}" style="font-family: Arial, sans-serif; color: ${purple[700]}; text-decoration: none;">${coach.email}</a>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                  ${
                    coach.phone
                      ? `
                  <tr>
                    <td height="4"></td>
                  </tr>
                  <tr>
                    <td>
                      <table cellpadding="0" cellspacing="0" border="0">
                        <tr>
                          <td valign="middle" width="20">
                            <img src="${phoneIconDataURL}" alt="Phone" width="16" height="16">
                          </td>
                          <td valign="middle">
                            <a href="tel:${coach.phone}" style="font-family: Arial, sans-serif; color: ${purple[700]}; text-decoration: none;">${coach.phone}</a>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                  `
                      : ""
                  }
                  <tr>
                    <td height="12"></td>
                  </tr>
                  <tr>
                    <td>
                      <table cellpadding="0" cellspacing="0" border="0">
                        <tr>
                          ${[
                            coach.profile?.website
                              ? `<td><a href="${coach.profile.website}"><img src="${websiteIconDataURL}" alt="Website" width="16" height="16"></a></td><td width="16"></td>`
                              : "",
                            coach.profile?.facebookProfile
                              ? `<td><a href="${coach.profile.facebookProfile}"><img src="${fbIconDataURL}" alt="Facebook" width="16" height="16"></a></td><td width="16"></td>`
                              : "",
                            coach.profile?.instagramProfile
                              ? `<td><a href="${coach.profile.instagramProfile}"><img src="${instagramIconDataURL}" alt="Instagram" width="16" height="16"></a></td><td width="16"></td>`
                              : "",
                            coach.profile?.linkedinProfile
                              ? `<td><a href="${coach.profile.linkedinProfile}"><img src="${linkedinIconDataURL}" alt="LinkedIn" width="16" height="16"></a></td><td width="16"></td>`
                              : "",
                            coach.profile?.youtubeChannel
                              ? `<td><a href="${coach.profile.youtubeChannel}"><img src="${youtubeIconDataURL}" alt="YouTube" width="16" height="16"></a></td><td width="16"></td>`
                              : "",
                            `<td><a href="https://dreamcoachmatch.com/${coach.username}"><img src="${dreamCoachMatchIconDataURL}" alt="Dream Coach Match" width="16" height="16"></a></td>`,
                          ]
                            .filter(Boolean)
                            .join("")}
                        </tr>
                      </table>
                    </td>
                  </tr>
                  <tr>
                    <td height="12"></td>
                  </tr>
                  <tr>
                    <td>
                      <table cellpadding="8" cellspacing="0" border="0" bgcolor="${gray[50]}" style="border-radius: 6px;">
                        <tr>
                          <td>
                            <a href="https://dreamcoachmatch.com/${coach.username}" style="font-family: Arial, sans-serif; color: ${purple[700]}; text-decoration: none; font-weight: 500;">
                              <table cellpadding="0" cellspacing="0" border="0">
                                <tr>
                                  <td valign="middle" width="24">
                                    <img src="${dreamCoachMatchIconDataURL}" alt="Dream Coach Match" width="16" height="16">
                                  </td>
                                  <td valign="middle">
                                    View my Dream Coach Match Profile
                                  </td>
                                </tr>
                              </table>
                            </a>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `.trim();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Signature</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="rounded-lg border bg-white p-4">
            <div dangerouslySetInnerHTML={{ __html: signatureHtml }} />
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                if (hiddenDiv) {
                  // Insert the HTML into the hidden div
                  hiddenDiv.innerHTML = signatureHtml;

                  // Select the contents
                  const range = document.createRange();
                  range.selectNodeContents(hiddenDiv);
                  const selection = window.getSelection();
                  selection?.removeAllRanges();
                  selection?.addRange(range);

                  // Execute copy command
                  try {
                    document.execCommand("copy");
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 2000);
                  } catch {
                    // Copy failed - execCommand may not be supported in all browsers
                  } finally {
                    // Clean up
                    selection?.removeAllRanges();
                  }
                }
              }}
            >
              <Copy className="mr-2 h-4 w-4" />
              {copied ? "Copied!" : "Copy Signature"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
